import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { supportEmail } from "../../../constants";
import Loader from "../../../components/loader";
import SnackbarAlert from "../../../components/SnackBarAlert";
import { sanitizeLandingHtml } from "../../../utils/sanitize.landing.html";
import { isExpiryToday, isRegistrationClosed } from "../../../utils/dateUtils";
import {
  checkStudent,
  enrollStudent,
  forgot,
  httpStatusOf,
  registerStudent,
  resolvePublicSsClass,
} from "../../../services/SsClassPublicService";
import {
  classAccessSimulationPath,
  getValidEnrolmentSession,
  setEnrolmentSession,
} from "../../../utils/self.serve.enrolment-storage";
import {
  PublicSsClass,
  SsEnrollRequest,
  SsEnrollResponse,
} from "../../../types/self-serve.public.types";
import ClassLandingAccessCodeForm from "./ClassLandingAccessCodeForm";
import ClassLandingForgotCodeForm from "./ClassLandingForgotCodeForm";
import ClassLandingNameDialog from "./ClassLandingNameDialog";
import ClassLandingRevokeConfirmDialog from "./ClassLandingRevokeConfirmDialog";
import ClassPublicShell from "./ClassPublicShell";
import ClassUnavailableNotice from "./ClassUnavailableNotice";
import RegisterAndPay from "./register.and.pay";
import {
  CLIPPED_TABINDEX_ATTR,
  COLLAPSED_INSTRUCTION_LINES,
  COLLAPSED_INSTRUCTIONS_MAX_HEIGHT,
  syncClippedInstructionFocus,
  textLineBottoms,
} from "./class.landing.instructions";
import {
  CLOSED_ALERT,
  FORGOT_ACKNOWLEDGED_MESSAGE,
  FORGOT_INVALID_EMAIL_MESSAGE,
  FORGOT_SUBMIT_FAILED_MESSAGE,
  INVALID_LINK_MESSAGE,
  redeemErrorMessage,
  resolveErrorMessage,
  SsAccessSimulationLocationState,
  SsAlertState,
} from "./self.serve.alert";

/**
 * Public Self-Serve class landing — `/class/:slug`.
 * Enter access code checks the student, registers a bookstore student when needed, then enrols.
 * Register and pay lives in `register.and.pay.tsx`.
 * Loads the class, renders its instructions and the enabled tabs. A tab that already holds a
 * valid enrolment session for this class goes straight to Access Simulation.
 *
 * @see docs/edsims/modules/21-self-serve-enrollment.md
 * @see docs/edsims/modules/23-self-serve-enrollment-runtime.md
 */
const ClassLandingPage: React.FC<any> = (props) => {
  const slug = props.match?.params?.slug;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [clazz, setClazz] = useState<PublicSsClass | null>(null);
  const [tab, setTab] = useState<"pay" | "code" | "forgot">("code");
  const [instructionsExpanded, setInstructionsExpanded] = useState(false);
  const [instructionsOverflow, setInstructionsOverflow] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [alert, setAlert] = useState<SsAlertState>(CLOSED_ALERT);
  const [forgotEmailError, setForgotEmailError] = useState("");
  const [pendingEnrolment, setPendingEnrolment] = useState<SsEnrollRequest | null>(
    null
  );
  const [nameOpen, setNameOpen] = useState(false);
  const [revokeOpen, setRevokeOpen] = useState(false);
  const [previewMaxHeight, setPreviewMaxHeight] = useState<number | undefined>();
  const instructionsRef = useRef<HTMLDivElement | null>(null);

  // Load the class for this slug, pick the first enabled tab, and skip to Access Simulation
  // when this tab is already enrolled. `cancelled` ignores a response after unmount or slug change.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!slug) {
        setError(INVALID_LINK_MESSAGE);
        setLoading(false);
        return;
      }
      setLoading(true);
      setError("");
      try {
        const data = await resolvePublicSsClass(slug);
        if (cancelled) {
          return;
        }
        setClazz(data);
        const openEnterCode =
          new URLSearchParams(props.location?.search || "").get("tab") === "code" &&
          data.landingOptionEnterCode;
        if (openEnterCode) {
          setTab("code");
        } else if (data.landingOptionPay) {
          setTab("pay");
        } else if (data.landingOptionEnterCode) {
          setTab("code");
        } else if (data.landingOptionForgotCode) {
          setTab("forgot");
        }
        if (getValidEnrolmentSession(slug)) {
          props.history.replace(classAccessSimulationPath(slug));
          return;
        }
      } catch (e: any) {
        if (!cancelled) {
          setClazz(null);
          setError(resolveErrorMessage(e));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // A Forgot field error belongs to the email typed before; the form starts empty again.
  useEffect(() => {
    setForgotEmailError("");
  }, [tab]);

  /** Instructions HTML after DOMPurify; the only form ever rendered. */
  const safeInstructions = useMemo(
    () => sanitizeLandingHtml(clazz?.landingInstructions),
    [clazz?.landingInstructions]
  );

  // Measured rather than assumed: the toggle only shows when the content runs past the preview lines
  // or the height cap (media has no text lines). Clipped content still has layout boxes and a full
  // scrollHeight, so this stays accurate while collapsed.
  useLayoutEffect(() => {
    if (!clazz?.landingInstructions) {
      setInstructionsOverflow(false);
      setPreviewMaxHeight(undefined);
      return;
    }
    const el = instructionsRef.current;
    if (!el) {
      return;
    }
    const measure = () => {
      const bottoms = textLineBottoms(el);
      const tooManyLines = bottoms.length > COLLAPSED_INSTRUCTION_LINES;
      const tooTall = el.scrollHeight > COLLAPSED_INSTRUCTIONS_MAX_HEIGHT;
      const overflows = tooManyLines || tooTall;
      const lineCut = tooManyLines
        ? Math.ceil(bottoms[COLLAPSED_INSTRUCTION_LINES - 1])
        : COLLAPSED_INSTRUCTIONS_MAX_HEIGHT;
      setInstructionsOverflow(overflows);
      setPreviewMaxHeight(
        overflows ? Math.min(lineCut, COLLAPSED_INSTRUCTIONS_MAX_HEIGHT) : undefined
      );
    };
    measure();
    if (typeof ResizeObserver === "undefined") {
      return;
    }
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    // The wrapper's box is fixed while clipped; watch the content too so late-loading media re-measures.
    if (el.firstElementChild) {
      observer.observe(el.firstElementChild);
    }
    return () => observer.disconnect();
  }, [clazz?.landingInstructions, safeInstructions]);

  // Overflow only clips the preview visually. Links below that cut stay tabbable unless removed here.
  useLayoutEffect(() => {
    const el = instructionsRef.current;
    if (!el) {
      return;
    }
    const collapsed = instructionsOverflow && !instructionsExpanded;
    syncClippedInstructionFocus(el, collapsed);
    return () => syncClippedInstructionFocus(el, false);
  }, [instructionsExpanded, instructionsOverflow, previewMaxHeight, safeInstructions]);

  /**
   * Stores the enrolment session from a successful enroll (signing out other tabs that hold a
   * different student), resets the form and dialogs, and opens Access Simulation, which shows
   * the success message.
   */
  const goToAccessSimulation = (result: SsEnrollResponse) => {
    if (!slug || !result?.enrolmentToken || result.stdId == null || result.classId == null) {
      showError("We could not start your session. Please try again.");
      return;
    }
    setEnrolmentSession(slug, {
      enrolmentToken: result.enrolmentToken,
      stdId: result.stdId,
      classId: result.classId,
    });
    setAlert(CLOSED_ALERT);
    setNameOpen(false);
    setRevokeOpen(false);
    setPendingEnrolment(null);
    const locationState: SsAccessSimulationLocationState = { redeemed: true };
    props.history.push(classAccessSimulationPath(slug), locationState);
  };

  /** Shows an API error in the alert box. */
  const showError = (msg: string) => setAlert({ open: true, type: "error", msg });

  /**
   * Shared failure path for check, register, and enroll.
   * 404 / 410 replace the page. Anything else, including `SS_STUDENT_NOT_FOUND`, stays in the
   * alert box so an open dialog can be corrected and resubmitted. An unknown email and an
   * invalid code share one generic message; the inputs are not marked.
   */
  const showFlowError = (err: any) => {
    if (err?.response?.status === 404 || err?.response?.status === 410) {
      setError(resolveErrorMessage(err));
      setClazz(null);
      return;
    }
    showError(redeemErrorMessage(err));
  };

  /**
   * Enrols an existing student. `SS_REDEEM_CONFIRM_REVOKE` keeps the payload and opens the
   * replace-code dialog. Callers own the submitting flag.
   */
  const applyEnroll = async (payload: SsEnrollRequest) => {
    if (!slug) {
      return;
    }
    try {
      const result = await enrollStudent(slug, payload);
      goToAccessSimulation(result);
    } catch (err: any) {
      if (err?.errorCode === "SS_REDEEM_CONFIRM_REVOKE") {
        setPendingEnrolment(payload);
        setNameOpen(false);
        setRevokeOpen(true);
      } else {
        showFlowError(err);
      }
    }
  };

  /** Replace-code dialog: enrol again with `confirmRevoke`. */
  const runEnroll = async (payload: SsEnrollRequest) => {
    setSubmitting(true);
    setAlert(CLOSED_ALERT);
    try {
      await applyEnroll(payload);
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Enter access code: check the student first. Bookstore with no student opens registration.
   * An existing student goes straight to enroll. Student Pay with no student shows the API error.
   */
  const handleAccessCodeSubmit = async (email: string, code: string) => {
    if (!slug) {
      return;
    }
    setSubmitting(true);
    setAlert(CLOSED_ALERT);
    try {
      const result = await checkStudent(slug, { email, code });
      if (result.registrationRequired) {
        setPendingEnrolment({ email, code });
        setNameOpen(true);
        return;
      }
      await applyEnroll({ email, code });
    } catch (err: any) {
      showFlowError(err);
    } finally {
      setSubmitting(false);
    }
  };

  /** Registers the bookstore student, then enrols them with the same email and code. */
  const handleRegister = async (studentName: string) => {
    if (!slug || !pendingEnrolment) {
      return;
    }
    setSubmitting(true);
    setAlert(CLOSED_ALERT);
    try {
      await registerStudent(slug, { ...pendingEnrolment, studentName });
      await applyEnroll(pendingEnrolment);
    } catch (err: any) {
      showFlowError(err);
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Forgot: every HTTP 200 shows the same acknowledgment in the snackbar, whether or not
   * mail was sent. HTTP 400 marks the email; any other failure shows fixed submit-failed copy.
   * The API message is never shown on this public form.
   */
  const handleForgotSubmit = async (email: string) => {
    if (!slug) {
      return;
    }
    setAlert(CLOSED_ALERT);
    setForgotEmailError("");
    try {
      await forgot(slug, { email });
      setAlert({ open: true, type: "success", msg: FORGOT_ACKNOWLEDGED_MESSAGE });
    } catch (err: unknown) {
      setForgotEmailError(
        httpStatusOf(err) === 400
          ? FORGOT_INVALID_EMAIL_MESSAGE
          : FORGOT_SUBMIT_FAILED_MESSAGE
      );
    }
  };

  if (loading) {
    return (
      <ClassPublicShell>
        <div className="overlay" role="status" aria-live="polite" aria-label="Loading class">
          <Loader loading={true} />
        </div>
      </ClassPublicShell>
    );
  }

  if (!clazz) {
    return (
      <ClassPublicShell title="Class unavailable">
        <div className="ss-public-card">
          <ClassUnavailableNotice message={error || "Class not available."} />
        </div>
      </ClassPublicShell>
    );
  }

  const instructionsCollapsed = instructionsOverflow && !instructionsExpanded;
  const expiryToday = isExpiryToday(clazz.expiryDate);
  const registrationClosed = isRegistrationClosed(clazz.expiryDate);
  const showPay = !!clazz.landingOptionPay;
  const hasAnyOption =
    showPay ||
    !!clazz.landingOptionEnterCode ||
    !!clazz.landingOptionForgotCode;
  /** Enabled tabs in display order; used for arrow-key navigation. */
  const availableTabs = (
    [
      clazz.landingOptionPay ? "pay" : null,
      clazz.landingOptionEnterCode ? "code" : null,
      clazz.landingOptionForgotCode ? "forgot" : null,
    ] as const
  ).filter((value): value is "pay" | "code" | "forgot" => value != null);

  /** Left/Right arrow moves between enabled tabs (wrapping) and focuses the new tab. */
  const handleTabKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== "ArrowRight" && event.key !== "ArrowLeft") {
      return;
    }
    if (availableTabs.length < 2) {
      return;
    }
    const current = availableTabs.indexOf(tab);
    const delta = event.key === "ArrowRight" ? 1 : -1;
    const next =
      availableTabs[(current + delta + availableTabs.length) % availableTabs.length];
    event.preventDefault();
    setTab(next);
    requestAnimationFrame(() => {
      document.getElementById(`ss-tab-${next}`)?.focus();
    });
  };

  return (
    <ClassPublicShell title={clazz.name} titleAlign="center">
      {clazz.landingInstructions ? (
        <div className="ss-public-card ss-public-instructions">
          <div
            id="class-landing-instructions"
            ref={instructionsRef}
            className={
              instructionsCollapsed
                ? "ss-public-instructions-preview"
                : undefined
            }
            style={
              instructionsCollapsed && previewMaxHeight != null
                ? { maxHeight: previewMaxHeight }
                : undefined
            }
          >
            <div dangerouslySetInnerHTML={{ __html: safeInstructions }} />
          </div>
          {instructionsOverflow ? (
            <button
              type="button"
              className="ss-public-more"
              aria-expanded={instructionsExpanded}
              aria-controls="class-landing-instructions"
              onClick={() => {
                const root = instructionsRef.current;
                const reveal = !instructionsExpanded
                  ? (root?.querySelector(
                      `[${CLIPPED_TABINDEX_ATTR}]`
                    ) as HTMLElement | null)
                  : null;
                setInstructionsExpanded((open) => !open);
                if (reveal) {
                  requestAnimationFrame(() => reveal.focus());
                }
              }}
            >
              {instructionsExpanded ? "See less" : "See more"}
            </button>
          ) : null}
        </div>
      ) : null}

      {!hasAnyOption ? (
        <div className="ss-public-card">
          <div className="alert alert-info mb-0" role="status">
            Enrolment is not available for this class. Please contact{" "}
            <a href={`mailto:${supportEmail}`}>{supportEmail}</a> for further
            assistance.
          </div>
        </div>
      ) : (
        <div className="ss-public-card">
          <div
            className="ss-public-tabs"
            role="tablist"
            aria-label="Class enrollment options"
            onKeyDown={handleTabKeyDown}
          >
            {showPay ? (
              <button
                type="button"
                id="ss-tab-pay"
                role="tab"
                aria-selected={tab === "pay"}
                aria-controls="ss-panel-pay"
                tabIndex={tab === "pay" ? 0 : -1}
                className={tab === "pay" ? "active" : ""}
                onClick={() => setTab("pay")}
              >
                Register and pay
              </button>
            ) : null}
            {clazz.landingOptionEnterCode ? (
              <button
                type="button"
                id="ss-tab-code"
                role="tab"
                aria-selected={tab === "code"}
                aria-controls="ss-panel-code"
                tabIndex={tab === "code" ? 0 : -1}
                className={tab === "code" ? "active" : ""}
                onClick={() => setTab("code")}
              >
                Enter access code
              </button>
            ) : null}
            {clazz.landingOptionForgotCode ? (
              <button
                type="button"
                id="ss-tab-forgot"
                role="tab"
                aria-selected={tab === "forgot"}
                aria-controls="ss-panel-forgot"
                tabIndex={tab === "forgot" ? 0 : -1}
                className={tab === "forgot" ? "active" : ""}
                onClick={() => setTab("forgot")}
              >
                Forgot your code?
              </button>
            ) : null}
          </div>

          {tab === "pay" && showPay ? (
            <div
              role="tabpanel"
              id="ss-panel-pay"
              aria-labelledby="ss-tab-pay"
            >
              {expiryToday ? (
                <ClassUnavailableNotice message="Class not available." />
              ) : registrationClosed ? (
                <ClassUnavailableNotice message="This class is no longer accepting registrations." />
              ) : (
                <RegisterAndPay clazz={clazz} slug={slug} />
              )}
            </div>
          ) : null}

          {tab === "code" ? (
            <div
              role="tabpanel"
              id="ss-panel-code"
              aria-labelledby="ss-tab-code"
            >
              <ClassLandingAccessCodeForm
                submitting={submitting}
                onSubmit={handleAccessCodeSubmit}
              />
            </div>
          ) : null}

          {tab === "forgot" && clazz.landingOptionForgotCode ? (
            <div
              role="tabpanel"
              id="ss-panel-forgot"
              aria-labelledby="ss-tab-forgot"
            >
              <ClassLandingForgotCodeForm
                submitError={forgotEmailError}
                onClearSubmitError={() => setForgotEmailError("")}
                onSubmit={handleForgotSubmit}
              />
            </div>
          ) : null}
        </div>
      )}

      <ClassLandingNameDialog
        open={nameOpen}
        submitting={submitting}
        onCancel={() => {
          setNameOpen(false);
        }}
        onConfirm={handleRegister}
      />
      <ClassLandingRevokeConfirmDialog
        open={revokeOpen}
        submitting={submitting}
        onCancel={() => {
          setRevokeOpen(false);
        }}
        onConfirm={() => {
          if (pendingEnrolment) {
            runEnroll({ ...pendingEnrolment, confirmRevoke: true });
          }
        }}
      />
      {/* API errors from the forms and both dialogs, and the Forgot acknowledgment. */}
      <SnackbarAlert
        openAlert={alert.open}
        setOpenAlert={() => setAlert(CLOSED_ALERT)}
        type={alert.type}
        msg={alert.msg}
      />
    </ClassPublicShell>
  );
};

export default ClassLandingPage;
